import { useQuery } from '@tanstack/react-query';
import type { JackpotPool, JackpotProduct, JackpotStatistics, PaginatedData } from '../@types';
import { QUERY_KEYS } from '../constants';
import { API_ROUTES } from '../constants/apiRoutes';
import { PRODUCT } from '../types/enums';
import { getData } from '../utils/api';

export const useJackpotPoolStatistics = (jackpotId?: string, enabled = true) =>
  useQuery({
    queryKey: [QUERY_KEYS.jackpots, 'pool', jackpotId],
    queryFn: (): Promise<JackpotStatistics> => getData(API_ROUTES.JACKPOTS.POOL_STATISTICS(jackpotId as string)),
    enabled: enabled && !!jackpotId,
  });

export const useCasinoJackpotPools = (enabled = true) =>
  useQuery({
    queryKey: [QUERY_KEYS.jackpots, PRODUCT.CASINO],
    queryFn: (): Promise<PaginatedData<JackpotPool>> =>
      getData(
        API_ROUTES.JACKPOTS.POOLS,
        {
          page: 1,
          limit: 100,
          product: PRODUCT.CASINO,
        },
        'wallet'
      ),
    select: (data) => data.items,
    enabled,
    refetchInterval: 30 * 1000,
  });

export const useCasinoJackpotPoolStatistics = (jackpotId?: string, product?: JackpotProduct, enabled = true) =>
  useQuery({
    queryKey: [QUERY_KEYS.jackpots, PRODUCT.CASINO, 'pool', jackpotId, product],
    queryFn: (): Promise<JackpotStatistics> =>
      getData(API_ROUTES.JACKPOTS.POOL_STATISTICS(jackpotId as string), { product }, 'wallet'),
    enabled: enabled && !!jackpotId && !!product,
  });

// Casino statistics come from wallet-api; sportsbook statistics are still served by the feed API.
export const useProductJackpotPoolStatistics = (pool: Pick<JackpotPool, 'jackpotId' | 'product'>, enabled = true) => {
  const isCasino = pool.product === PRODUCT.CASINO;
  const casinoStatistics = useCasinoJackpotPoolStatistics(pool.jackpotId, pool.product, enabled && isCasino);
  const sportsbookStatistics = useJackpotPoolStatistics(pool.jackpotId, enabled && !isCasino);

  return isCasino ? casinoStatistics : sportsbookStatistics;
};
